/**
 * @file: index.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useEffect } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { LoadingProps } from './types';

export const Loading = memo((props: LoadingProps & { visible: boolean }) => {
  const opacity = useSharedValue(0);
  const visible = props.visible;
  const { rpx } = useResponsiveContext();
  const mode = usePresentationMode();
  const presentationTheme = usePresentationTheme();
  const theme = mode === 'presentation' ? presentationTheme : undefined;
  const style = useAnimatedStyle(() => {
    return { opacity: opacity.value };
  });

  useEffect(() => {
    const targetOpacity = visible ? 1 : 0;
    opacity.value = withTiming(targetOpacity, {
      duration: 150,
      easing: Easing.inOut(Easing.linear),
    });
  }, [visible, opacity]);

  const pointerEvents = props.type === 'loading' ? 'auto' : 'none';

  return (
    <KeyboardAvoidingView
      style={StyleSheet.absoluteFill}
      pointerEvents={pointerEvents}
      css={`
        flex: 1;
        justify-content: center;
        align-items: center;
      `}
    >
      {props.backdrop !== false && visible && (
        <View style={StyleSheet.absoluteFill} />
      )}
      <Animated.View
        style={style}
        pointerEvents={pointerEvents}
        css={`
          background-color: #222222;
          width: ${rpx(mode === 'presentation' ? 200 : 80)}px;
          height: ${rpx(mode === 'presentation' ? 200 : 80)}px;
          border-radius: ${rpx(mode === 'presentation' ? 40 : 20)}px;
          align-items: center;
          justify-content: center;
        `}
      >
        {props.type === 'loading' ? (
          <ActivityIndicator
            size="large"
            color={theme?.toast.iconColor ?? '#fff'}
          />
        ) : (
          props.icon ?? (
            <Icon
              name={props.type === 'success' ? 'check' : 'warning'}
              color={theme?.toast.iconColor ?? '#fff'}
              size={
                mode === 'presentation'
                  ? props.type === 'success'
                    ? rpx(90)
                    : rpx(80)
                  : rpx(40)
              }
              css={`
                line-height: ${rpx(mode === 'presentation' ? 80 : 40)}px;
                margin-bottom: ${mode === 'presentation'
                  ? props.type === 'success'
                    ? rpx(0)
                    : rpx(4)
                  : 0}px;
                margin-top: ${mode === 'presentation' ? 0 : -4}px;
              `}
            />
          )
        )}
        {props.message ? (
          typeof props.message === 'object' ? (
            props.message
          ) : (
            <Text
              css={`
                color: ${theme?.toast.textColor ?? '#fff'};
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(mode === 'presentation' ? 30 : 14)}px;
                line-height: ${rpx(mode === 'presentation' ? 44 : 18)}px;
              `}
            >
              {props.message}
            </Text>
          )
        ) : null}
      </Animated.View>
    </KeyboardAvoidingView>
  );
});
